/**
 * TOSS Web Application - Messenger Store
 *
 * Zustand store for messenger client state:
 * - Active chat selection
 * - Typing indicators
 * - Online users
 * - Reply-to context
 * - Search state
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { ChatFolder, ChatMessage } from '@/types/messenger.types';
import type { SignalRConnectionState } from '@/types';

// ============================================================================
// Types
// ============================================================================

export interface TypingUser {
  userId: string;
  fullName: string;
  timestamp: number;
}

interface SelectionModeState {
  active: boolean;
  chatId: string | null;
  selectedIds: Set<string>;
}

interface PendingForwardState {
  targetChatId: string;
  messages: ChatMessage[];
}

interface MessengerState {
  activeChatId: string | null;
  activeFolder: Exclude<ChatFolder, 'archive'>;
  showArchive: boolean;
  typingUsers: Record<string, TypingUser[]>;
  onlineUsers: Set<string>;
  replyTo: ChatMessage | null;
  isSearchOpen: boolean;
  searchQuery: string;
  isInChatSearchOpen: boolean;
  inChatSearchQuery: string;
  scrollToMessageId: string | null;
  isAttachmentsPanelOpen: boolean;
  connectionState: SignalRConnectionState;
  selectionMode: SelectionModeState;
  pendingForward: PendingForwardState | null;
}

interface MessengerActions {
  setActiveChatId: (chatId: string | null) => void;
  setActiveFolder: (folder: Exclude<ChatFolder, 'archive'>) => void;
  toggleArchive: () => void;
  setTypingUser: (chatId: string, user: TypingUser) => void;
  removeTypingUser: (chatId: string, userId: string) => void;
  setOnlineUsers: (userIds: string[]) => void;
  addOnlineUser: (userId: string) => void;
  removeOnlineUser: (userId: string) => void;
  setReplyTo: (message: ChatMessage | null) => void;
  setSearchOpen: (open: boolean) => void;
  setSearchQuery: (query: string) => void;
  setInChatSearchOpen: (open: boolean) => void;
  setInChatSearchQuery: (query: string) => void;
  setScrollToMessageId: (messageId: string | null) => void;
  setAttachmentsPanelOpen: (open: boolean) => void;
  setConnectionState: (state: SignalRConnectionState) => void;
  enterSelection: (chatId: string, initialMessageId: string) => void;
  toggleSelected: (messageId: string) => void;
  exitSelection: () => void;
  setPendingForward: (payload: PendingForwardState) => void;
  clearPendingForward: () => void;
  reset: () => void;
}

type MessengerStore = MessengerState & MessengerActions;

// ============================================================================
// Initial State
// ============================================================================

const initialSelectionMode: SelectionModeState = {
  active: false,
  chatId: null,
  selectedIds: new Set<string>(),
};

const initialState: MessengerState = {
  activeChatId: null,
  activeFolder: 'chats',
  showArchive: false,
  typingUsers: {},
  onlineUsers: new Set<string>(),
  replyTo: null,
  isSearchOpen: false,
  searchQuery: '',
  isInChatSearchOpen: false,
  inChatSearchQuery: '',
  scrollToMessageId: null,
  isAttachmentsPanelOpen: false,
  connectionState: 'Disconnected' as SignalRConnectionState,
  selectionMode: initialSelectionMode,
  pendingForward: null,
};

// ============================================================================
// Store
// ============================================================================

export const useMessengerStore = create<MessengerStore>()((set) => ({
  ...initialState,

  setActiveChatId: (chatId) => {
    set({
      activeChatId: chatId,
      replyTo: null,
      isInChatSearchOpen: false,
      inChatSearchQuery: '',
      scrollToMessageId: null,
      isAttachmentsPanelOpen: false,
      // Reset selection mode when switching chats, but NOT pendingForward
      selectionMode: initialSelectionMode,
    });
  },

  setActiveFolder: (folder) => {
    set({ activeFolder: folder, showArchive: false });
  },

  toggleArchive: () => {
    set((state) => ({ showArchive: !state.showArchive }));
  },

  setTypingUser: (chatId, user) => {
    set((state) => {
      const chatTyping = state.typingUsers[chatId] ?? [];
      const existing = chatTyping.findIndex((u) => u.userId === user.userId);
      const updated =
        existing >= 0
          ? chatTyping.map((u, i) => (i === existing ? user : u))
          : [...chatTyping, user];
      return {
        typingUsers: {
          ...state.typingUsers,
          [chatId]: updated,
        },
      };
    });
  },

  removeTypingUser: (chatId, userId) => {
    set((state) => {
      const chatTyping = state.typingUsers[chatId] ?? [];
      return {
        typingUsers: {
          ...state.typingUsers,
          [chatId]: chatTyping.filter((u) => u.userId !== userId),
        },
      };
    });
  },

  setOnlineUsers: (userIds) => {
    set({ onlineUsers: new Set(userIds) });
  },

  addOnlineUser: (userId) => {
    set((state) => {
      const next = new Set(state.onlineUsers);
      next.add(userId);
      return { onlineUsers: next };
    });
  },

  removeOnlineUser: (userId) => {
    set((state) => {
      const next = new Set(state.onlineUsers);
      next.delete(userId);
      return { onlineUsers: next };
    });
  },

  setReplyTo: (message) => {
    set({ replyTo: message });
  },

  setSearchOpen: (open) => {
    set({ isSearchOpen: open, searchQuery: open ? '' : '' });
  },

  setSearchQuery: (query) => {
    set({ searchQuery: query });
  },

  setInChatSearchOpen: (open) => {
    set({ isInChatSearchOpen: open, inChatSearchQuery: '' });
  },

  setInChatSearchQuery: (query) => {
    set({ inChatSearchQuery: query });
  },

  setScrollToMessageId: (messageId) => {
    set({ scrollToMessageId: messageId });
  },

  setAttachmentsPanelOpen: (open) => {
    set({ isAttachmentsPanelOpen: open });
  },

  setConnectionState: (connectionState) => {
    set({ connectionState });
  },

  enterSelection: (chatId, initialMessageId) => {
    set({
      selectionMode: {
        active: true,
        chatId,
        selectedIds: new Set([initialMessageId]),
      },
    });
  },

  toggleSelected: (messageId) => {
    set((state) => {
      const { selectedIds, chatId } = state.selectionMode;
      const next = new Set([...selectedIds]);
      if (next.has(messageId)) {
        next.delete(messageId);
        if (next.size === 0) {
          return { selectionMode: { active: false, chatId, selectedIds: next } };
        }
      } else {
        if (next.size >= 100) {
          // Limit reached — caller should show toast
          return state;
        }
        next.add(messageId);
      }
      return { selectionMode: { ...state.selectionMode, selectedIds: next } };
    });
  },

  exitSelection: () => {
    set({ selectionMode: initialSelectionMode });
  },

  setPendingForward: (payload) => {
    set({ pendingForward: payload });
  },

  clearPendingForward: () => {
    set({ pendingForward: null });
  },

  reset: () => {
    set(initialState);
  },
}));

// ============================================================================
// Selectors
// ============================================================================

export function useActiveChatId(): string | null {
  return useMessengerStore((state) => state.activeChatId);
}

const EMPTY_TYPING_USERS: TypingUser[] = [];

export function useTypingUsersForChat(chatId: string): TypingUser[] {
  return useMessengerStore((state) => state.typingUsers[chatId] ?? EMPTY_TYPING_USERS);
}

export function useIsUserOnline(userId: string): boolean {
  return useMessengerStore((state) => state.onlineUsers.has(userId));
}

export function useActiveFolder() {
  return useMessengerStore((state) => state.activeFolder);
}

export function useShowArchive() {
  return useMessengerStore((state) => state.showArchive);
}

export function useMessengerActions() {
  return useMessengerStore(
    useShallow((state) => ({
      setActiveChatId: state.setActiveChatId,
      setActiveFolder: state.setActiveFolder,
      toggleArchive: state.toggleArchive,
      setTypingUser: state.setTypingUser,
      removeTypingUser: state.removeTypingUser,
      setOnlineUsers: state.setOnlineUsers,
      addOnlineUser: state.addOnlineUser,
      removeOnlineUser: state.removeOnlineUser,
      setReplyTo: state.setReplyTo,
      setSearchOpen: state.setSearchOpen,
      setSearchQuery: state.setSearchQuery,
      setInChatSearchOpen: state.setInChatSearchOpen,
      setInChatSearchQuery: state.setInChatSearchQuery,
      setScrollToMessageId: state.setScrollToMessageId,
      setAttachmentsPanelOpen: state.setAttachmentsPanelOpen,
      enterSelection: state.enterSelection,
      toggleSelected: state.toggleSelected,
      exitSelection: state.exitSelection,
      setPendingForward: state.setPendingForward,
      clearPendingForward: state.clearPendingForward,
      reset: state.reset,
    }))
  );
}

export default useMessengerStore;
